import React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useCatalog } from '../app/providers/CatalogProvider.jsx';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { useAccess } from '../app/providers/AccessProvider.jsx';
import { EpisodeDetailPage, TitleDetailPage } from './CatalogPages.jsx';
import { contentPlaybackId, trailerPlaybackId } from '../services/playback/playbackService.js';
import '../styles/playback.css';

function findTitle(catalog, id) {
  return catalog?.titles?.find((item) => item.id === id || item.slug === id) || null;
}

function LockedActions({ decision, titleId, returnTo, episode = null }) {
  const { user } = useSession();
  const actions = [];
  const reason = decision?.reason || '';
  if (!user && ['login', 'login_or_choose', 'tvod'].includes(reason)) {
    actions.push(<NavLink key="login" className="hero-button primary" to={`/login?return=${encodeURIComponent(returnTo)}`}>Log In</NavLink>);
  }
  if (['login', 'login_or_choose', 'upgrade', 'svod_or_rent', 'episode_or_season_rent'].includes(reason)) {
    actions.push(<NavLink key="subscribe" className="hero-button primary" to="/membership">Subscribe</NavLink>);
  }
  if (['login_or_choose', 'svod_or_rent', 'tvod', 'episode_or_season_rent'].includes(reason)) {
    const query = episode
      ? `?rentalScope=episode&episodeId=${encodeURIComponent(episode.id)}&seasonNumber=${encodeURIComponent(episode.season)}`
      : '';
    actions.push(<NavLink key="rent" className="hero-button secondary" to={`/checkout/${encodeURIComponent(titleId)}${query}`}>Rent</NavLink>);
  }
  return actions;
}

export function TitleDetailPlaybackEntryPage() {
  const { titleId } = useParams();
  const { catalog } = useCatalog();
  const access = useAccess();
  const item = findTitle(catalog, titleId);
  const contentId = contentPlaybackId(item);
  const trailerId = trailerPlaybackId(item);
  const decision = item ? access.titleAccess(item, 'content') : null;
  const returnTo = item ? `/title/${encodeURIComponent(item.id)}` : '/home';

  const heroActions = item ? (
    <div className="detail-playback-actions" aria-label={`${item.title} playback actions`} data-access-reason={decision?.reason || 'unknown'}>
      {contentId && decision?.allowed ? <NavLink className="hero-button primary" to={`/watch/${encodeURIComponent(item.id)}`}>▶ Play</NavLink> : null}
      {contentId && !decision?.allowed ? <LockedActions decision={decision} titleId={item.id} returnTo={returnTo} /> : null}
      {trailerId ? <NavLink className="hero-button secondary" to={`/trailer/${encodeURIComponent(item.id)}`}>Watch Trailer</NavLink> : null}
      {!contentId && !trailerId ? <span className="catalog-muted">Playback is not yet published for this title.</span> : null}
    </div>
  ) : null;

  return <TitleDetailPage actions={heroActions} />;
}

export function EpisodeDetailPlaybackEntryPage() {
  const { seriesId, season, episode } = useParams();
  const { catalog } = useCatalog();
  const access = useAccess();
  const series = findTitle(catalog, seriesId);
  const item = series?.episodes?.find((entry) => String(entry.season) === String(season) && String(entry.episode) === String(episode));
  const playbackId = contentPlaybackId(item);
  const decision = series && item ? access.episodeAccess(series, item) : null;
  const returnTo = series && item ? `/episode/${encodeURIComponent(series.id)}/${item.season}/${item.episode}` : '/home';

  return (
    <>
      <EpisodeDetailPage />
      {series && item ? (
        <div className="detail-playback-actions episode-actions" aria-label={`${item.title} playback actions`} data-access-reason={decision?.reason || 'unknown'}>
          {playbackId && decision?.allowed ? (
            <NavLink
              className="hero-button primary"
              to={`/watch/${encodeURIComponent(series.id)}/season/${item.season}/episode/${item.episode}`}
            >
              ▶ Play Episode
            </NavLink>
          ) : null}
          {playbackId && !decision?.allowed ? <LockedActions decision={decision} titleId={series.id} returnTo={returnTo} episode={item} /> : null}
          {!playbackId ? <span className="catalog-muted">Playback is not yet published for this episode.</span> : null}
        </div>
      ) : null}
    </>
  );
}
